import { useEffect, useRef, useState, type FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { Check, Loader2 } from "lucide-react";
import { sendSupportMessage, type SupportCategory } from "@/api/client";
import { Button, Drawer, EmptyState } from "@/components/ds";
import { APP_VERSION } from "@/lib/constants";
import { useAuthStore } from "@/stores/auth";
import { useSupportStore } from "@/stores/support";

const CATEGORY_OPTIONS: ReadonlyArray<{ value: SupportCategory; label: string }> = [
  { value: "bug", label: "Bug" },
  { value: "question", label: "Question" },
  { value: "idea", label: "Idea" },
  { value: "other", label: "Other" },
];

const DEFAULT_CATEGORY: SupportCategory = "bug";
const MIN_MESSAGE_LENGTH = 10;
const MAX_MESSAGE_LENGTH = 4000;
const FORM_ID = "support-form";

const LABEL_CLASS = "block text-xs font-medium text-ink-3";
// 16px on phones: iOS auto-zooms a focused field <16px, which breaks the
// full-screen drawer layout; 13.5px matches `.input` on desktop.
const FIELD_CLASS = "input mt-1 text-base md:text-[13.5px]";

/**
 * In-app Help channel: a short form that reaches the TRACE team by email
 * (POST /support/messages). Mounted once per shell — AppLayout on desktop,
 * MobileAppShell on phones — and opened through `useSupportStore` from the
 * BrandActions cluster and the mobile drawer.
 */
export function SupportDialog() {
  const isOpen = useSupportStore((s) => s.isOpen);
  const close = useSupportStore((s) => s.close);
  const email = useAuthStore((s) => s.user?.email);
  const { pathname } = useLocation();

  const [category, setCategory] = useState<SupportCategory>(DEFAULT_CATEGORY);
  const [message, setMessage] = useState("");
  const mutation = useMutation({ mutationFn: sendSupportMessage });
  const { reset: resetMutation } = mutation;

  // A fresh form every time the drawer opens. Resetting on the open
  // transition (rather than on close) keeps the slide-out animation from
  // flashing an empty form over the "Got it" state; the ref guards the body
  // so it runs once per open regardless of dependency identity.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setCategory(DEFAULT_CATEGORY);
      setMessage("");
      resetMutation();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetMutation]);

  const trimmed = message.trim();
  const pending = mutation.isPending;
  const canSend = !pending && trimmed.length >= MIN_MESSAGE_LENGTH;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSend) return;
    mutation.mutate({
      category,
      message: trimmed,
      page_url: window.location.href,
      app_version: APP_VERSION,
      user_agent: navigator.userAgent,
    });
  }

  const footer = mutation.isSuccess ? (
    <div className="flex items-center justify-end gap-2">
      <Button size="sm" variant="accent" onClick={close}>
        Close
      </Button>
    </div>
  ) : (
    <div className="flex items-center justify-end gap-2">
      <Button size="sm" variant="ghost" onClick={close} disabled={pending}>
        Cancel
      </Button>
      <Button
        size="sm"
        variant="accent"
        type="submit"
        form={FORM_ID}
        disabled={!canSend}
      >
        {pending ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Sending…
          </>
        ) : (
          "Send"
        )}
      </Button>
    </div>
  );

  return (
    <Drawer open={isOpen} onClose={close} title="Help" mobileFullscreen footer={footer}>
      {mutation.isSuccess ? (
        <EmptyState icon={Check} title="Got it" subtitle="We will reply by email." />
      ) : (
        <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-ink-3">
            Found a bug, have a question or an idea? Write to us — we reply by email.
          </p>

          <div>
            <label htmlFor="support-category" className={LABEL_CLASS}>
              Category
            </label>
            <select
              id="support-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as SupportCategory)}
              disabled={pending}
              className={FIELD_CLASS}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="support-message" className={LABEL_CLASS}>
              Message
            </label>
            <textarea
              id="support-message"
              rows={6}
              maxLength={MAX_MESSAGE_LENGTH}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              disabled={pending}
              placeholder="Describe what happened or what you need…"
              className={FIELD_CLASS}
            />
          </div>

          {mutation.isError && (
            <p
              role="alert"
              className="rounded-ds-xs bg-bad/10 px-3 py-2 text-xs font-medium text-bad"
            >
              {mutation.error.message || "Could not send your message. Please try again."}
            </p>
          )}

          <p className="break-words text-[11px] leading-relaxed text-ink-4">
            Sent from {pathname} · TRACE v{APP_VERSION} · {email}
          </p>
        </form>
      )}
    </Drawer>
  );
}
